// Home.js
// Sport selection home screen.
// On load, checks Firestore for today's completed drafts per sport.
// Cards show the team rating score box when completed — matching DraftSportCard in iOS.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import { useAuth } from "../managers/AuthContext";
import { getTodaysDraftScores, getTodaysDraftEntries, getTodaysStreakBests } from "../managers/leaderboardService";
import lightningIcon from "../assets/lightning-fill.svg";
import { getStreakStats } from "../managers/streakService";

const SPORTS = [
  { id: "football",   name: "Football",   hex: "#00b04f" },
  { id: "baseball",   name: "Baseball",   hex: "#db2626" },
  { id: "basketball", name: "Basketball", hex: "#ff8c00" },
  { id: "hockey",     name: "Hockey",     hex: "#0da6e8" },
  { id: "soccer",     name: "Soccer",     hex: "#1a66e6" },
];

// Maps each sport to its slash-panel logo. (Basketball uses the dribbble logo.)
const SPORT_ICONS = {
  football:   footballIcon,
  baseball:   baseballIcon,
  basketball: basketballIcon,
  hockey:     hockeyIcon,
  soccer:     soccerIcon,
};

// ─── Sport icon ───────────────────────────────────────────────────────────────
// Rendered black on the sport-colored slash panel via the panelIcon filter.

function SportIcon({ sport }) {
  return <img src={SPORT_ICONS[sport.id]} alt={sport.name} style={styles.panelIcon} />;
}

// ─── Sport row ────────────────────────────────────────────────────────────────

function SportRow({ sport, todayScore, streakBest, onClick, onStreakClick }) {
  const [isPressed, setIsPressed] = useState(false);
  const hasCompleted = todayScore !== null && todayScore !== undefined;

  return (
    <div style={styles.sportSection}>
      {/* Sport name header with gradient lines */}
      <div style={styles.sportHeader}>
        <div style={{
          ...styles.headerLine,
          background: `linear-gradient(to left, ${sport.hex}8c, transparent)`,
        }} />
        <span style={{ ...styles.sportHeaderText, color: sport.hex }}>
          {sport.name.toUpperCase()}
        </span>
        <div style={{
          ...styles.headerLine,
          background: `linear-gradient(to right, ${sport.hex}8c, transparent)`,
        }} />
      </div>

      {/* Draft card (wide) + Streak card (narrow) side by side */}
      <div style={styles.cardRow}>
        {/* Draft card */}
        <div
          style={{
            ...styles.draftCard,
            flex: 1,
            // Completed: sport-color border + glow shadow, matching iOS
            borderColor: hasCompleted ? `${sport.hex}cc` : `${sport.hex}47`,
            boxShadow: hasCompleted
              ? `0 5px 18px ${sport.hex}4d, 0 2px 6px rgba(0,0,0,0.25)`
              : "0 2px 6px rgba(0,0,0,0.25)",
            transform: isPressed ? "scale(0.97)" : "scale(1)",
            transition: "transform 0.1s ease",
          }}
          onClick={onClick}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          onMouseLeave={() => setIsPressed(false)}
        >
          {/* Left colored slash panel — dims slightly when completed */}
          <div style={{
            ...styles.slashPanel,
            background: sport.hex,
            opacity: hasCompleted ? 0.55 : 1,
          }}>
            <SportIcon sport={sport} />
          </div>

          {/* Right content — score box if completed, DRAFT label if not */}
          <div style={styles.draftCardRight}>
            {hasCompleted ? (
              <div style={{
                ...styles.scoreBox,
                background: `${sport.hex}52`,
                border: `1.5px solid ${sport.hex}cc`,
              }}>
                <span style={styles.scoreText}>{todayScore}</span>
              </div>
            ) : (
              <span style={styles.draftLabel}>DRAFT</span>
            )}
          </div>
        </div>

        {/* Streak card */}
        <StreakCard sport={sport} streakBest={streakBest} onClick={onStreakClick} />
      </div>
    </div>
  );
}

// ─── Streak card ────────────────────────────────────────────────────────────────
// Narrow card beside the draft card. Mirrors StreakSportCard.swift:
//   • Not played today → sport-color fill, lightning icon + "STREAKS".
//   • Played today      → dark fill, sport border, today's best streak in a box.

function StreakCard({ sport, streakBest, onClick }) {
  const [isPressed, setIsPressed] = useState(false);
  const playedToday = streakBest !== null && streakBest !== undefined && streakBest > 0;

  return (
    <div
      style={{
        ...styles.streakCard,
        background: playedToday ? "rgb(18, 22, 33)" : sport.hex,
        border: playedToday ? `1.5px solid ${sport.hex}a6` : "1.5px solid transparent",
        boxShadow: playedToday
          ? `0 5px 18px ${sport.hex}40, 0 2px 6px rgba(0,0,0,0.25)`
          : "0 2px 6px rgba(0,0,0,0.25)",
        transform: isPressed ? "scale(0.95)" : "scale(1)",
        transition: "transform 0.1s ease",
      }}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {playedToday ? (
        <div style={{ ...styles.streakScoreBox, background: `${sport.hex}52`, border: `1.5px solid ${sport.hex}cc` }}>
          <span style={styles.streakScoreText}>{streakBest}</span>
        </div>
      ) : (
        <div style={styles.streakUnplayed}>
          <img src={lightningIcon} alt="" style={styles.streakIcon} />
          <span style={styles.streakLabel}>STREAKS</span>
        </div>
      )}
    </div>
  );
}

// ─── Home screen ─────────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isGuest, freePlay } = useAuth();
  const [todayScores, setTodayScores] = useState({});
  const [streakBests, setStreakBests] = useState({});

  const ownerId = user?.uid || "guest";

  // Today's best streak per sport. Signed-in: from Firestore (midnight-Pacific,
  // cross-device). Guest: local fallback. Recomputed on focus so a streak just
  // played shows on the card when returning home.
  const loadStreakBests = useCallback(async () => {
    if (user) {
      try {
        const bests = await getTodaysStreakBests(user.uid);
        setStreakBests(bests);
        return;
      } catch (err) {
        console.warn("Could not load streak bests:", err);
      }
    }
    const map = {};
    SPORTS.forEach((s) => { map[s.id] = getStreakStats(ownerId, s.id).todaysBest; });
    setStreakBests(map);
  }, [user, ownerId]);

  const loadScores = useCallback(async () => {
    if (!user) return;
    try {
      const scores = await getTodaysDraftScores(user.uid);
      setTodayScores(scores);
    } catch (err) {
      console.warn("Could not load today's scores:", err);
    }
  }, [user]);

  // Load on mount and whenever we navigate back to this screen
  useEffect(() => {
    loadScores();
    loadStreakBests();
  }, [loadScores, loadStreakBests, location.key]);

  function handleStreakTap(sport) {
    navigate("/streak-overview", { state: { sport } });
  }

  async function handleSportTap(sport) {
    const score = todayScores[sport.id];
    const isCompleted = score !== null && score !== undefined;

    // Already drafted this sport today → reopen the completed view with all of
    // today's drafts (most recent first, swipeable). Starting a new draft would
    // be blocked by the daily limit anyway.
    // During Free Play we skip this so a fresh (uncounted) draft can begin even
    // when a real draft already exists for today.
    if (isCompleted && user && !freePlay) {
      try {
        const entries = await getTodaysDraftEntries(user.uid, sport.id);
        if (entries.length > 0) {
          navigate("/draft-complete", {
            state: { sport, entries, viewOnly: true },
          });
          return;
        }
      } catch (err) {
        console.warn("Could not load completed drafts:", err);
      }
    }

    // Otherwise show the draft overview (formation + chemistry), then the draft
    navigate("/draft-overview", { state: { sport } });
  }

  return (
    <div style={styles.container}>
      <div style={{ paddingTop: "12px" }} />

      <div style={styles.sportsList}>
        {SPORTS.map((sport) => (
          <SportRow
            key={sport.id}
            sport={sport}
            todayScore={freePlay ? null : (todayScores[sport.id] ?? null)}
            streakBest={freePlay ? 0 : (streakBests[sport.id] ?? 0)}
            onClick={() => handleSportTap(sport)}
            onStreakClick={() => handleStreakTap(sport)}
          />
        ))}
      </div>

      <p style={styles.footerText}>Drafts reset at midnight PST</p>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  container: {
    padding: "0 14px 24px",
    maxWidth: "480px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
  },
  sportsList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  sportSection: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },
  sportHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  headerLine: {
    flex: 1,
    height: "1px",
  },
  sportHeaderText: {
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "2.5px",
    whiteSpace: "nowrap",
  },
  cardRow: {
    display: "flex",
    gap: "8px",
    alignItems: "stretch",
  },
  streakCard: {
    width: "84px",
    height: "84px",
    flexShrink: 0,
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    cursor: "pointer",
    userSelect: "none",
  },
  streakUnplayed: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
  },
  streakIcon: {
    width: "30px",
    height: "30px",
    filter: "brightness(0) opacity(0.92)",   // black icon on the sport-color fill
  },
  streakLabel: {
    fontSize: "11px",
    fontWeight: "800",
    fontStyle: "italic",
    color: "rgba(0,0,0,0.92)",
    letterSpacing: "1px",
  },
  streakScoreBox: {
    width: "52px",
    height: "52px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  streakScoreText: {
    fontSize: "26px",
    fontWeight: "900",
    color: "#ffffff",
    letterSpacing: "-0.5px",
  },
  draftCard: {
    position: "relative",
    height: "84px",
    borderRadius: "14px",
    border: "1.5px solid",
    background: "rgb(18, 22, 33)",
    display: "flex",
    overflow: "hidden",
    cursor: "pointer",
    userSelect: "none",
  },
  // Absolute overlay on the left so the DRAFT label / score box can center
  // across the FULL card width (aligned under the centered sport title).
  slashPanel: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "28%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    clipPath: "polygon(0 0, 100% 0, 72% 100%, 0 100%)",
    transition: "opacity 0.2s ease",
    zIndex: 0,
  },
  panelIcon: {
    width: "62px",
    height: "62px",
    marginLeft: "-8px",
    filter: "brightness(0) opacity(0.88)",
  },
  draftCardRight: {
    flex: 1,
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // The draft card now shares the row with the 84px streak card (+8px gap), so
    // it sits left of the row's center. Padding the centering region by that same
    // 92px shifts DRAFT / the score box back under the centered sport title.
    paddingLeft: "92px",
  },
  draftLabel: {
    fontSize: "20px",
    fontWeight: "900",
    fontStyle: "italic",
    color: "rgba(255,255,255,0.88)",
    letterSpacing: "5px",
    // Counter the trailing letter-spacing so the glyphs sit truly centered
    paddingLeft: "5px",
  },
  // Score box — mirrors ZStack in DraftSportCard.swift
  scoreBox: {
    width: "72px",
    height: "56px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  scoreText: {
    fontSize: "22px",
    fontWeight: "900",
    color: "#ffffff",
    letterSpacing: "-0.5px",
  },
  footerText: {
    textAlign: "center",
    fontSize: "11px",
    color: "#606078",
    marginTop: "16px",
  },
};
